import React, { useState, useEffect } from "react";
import {
  X,
  Wallet,
  Calendar,
  Info,
  Users,
  User,
  Fingerprint,
  DollarSign,
  CheckCircle,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import moment from "moment";
import { useTranslation } from "react-i18next";

export default function BossCreateMonthlySalary({
  visible,
  onClose,
  onSuccess,
  placeId,
  placeName,
}) {
  const { t } = useTranslation();
  const [allData, setAllData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [choosedMonth, setChoosedMonth] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load from localStorage
  useEffect(() => {
    const data = localStorage.getItem("allData");
    const user = localStorage.getItem("user");
    const month = localStorage.getItem("choosedMonth");
    if (data) setAllData(JSON.parse(data));
    if (user) setUserData(JSON.parse(user));
    if (month) setChoosedMonth(month);
  }, []);

  // Reset selection when modal opens
  useEffect(() => {
    if (visible) setSelectedEmployees([]);
  }, [visible]);

  const toggleEmployee = (employee) => {
    setSelectedEmployees((prev) => {
      const exists = prev.find((e) => e.id === employee.id);
      return exists
        ? prev.filter((e) => e.id !== employee.id)
        : [...prev, employee];
    });
  };

  const isEmployeeSelected = (id) => selectedEmployees.some((e) => e.id === id);

  const handleSubmit = async () => {
    if (selectedEmployees.length === 0) {
      alert("Please select at least one employee");
      return;
    }
    if (!choosedMonth) {
      alert("Please select a month first");
      return;
    }
    if (!userData) {
      alert("User data not found");
      return;
    }

    setLoading(true);
    try {
      const [month, year] = choosedMonth.split("-");
      const dateStr = `01-${month}-${year}`;

      const promises = selectedEmployees.map((employee) =>
        axios.post(`${MyDomain}MonthlySalary/CreateMonthlySalary`, {
          UserId: userData.id,
          EmployeeId: employee.id,
          PlaceId: placeId,
          Date: dateStr,
        }),
      );

      await Promise.all(promises);

      alert(
        `Monthly salary recorded for ${selectedEmployees.length} ${
          selectedEmployees.length === 1 ? "employee" : "employees"
        }`,
      );
      setSelectedEmployees([]);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("❌ Error creating monthly salary:", error);
      alert(error.response?.data?.message || "Failed to record monthly salary");
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = selectedEmployees.reduce(
    (sum, emp) => sum + (emp.monthlySalary || 0),
    0,
  );

  if (!visible || !allData) return null;

  const monthlyEmployees = (allData.myEmployees || []).filter(
    (emp) => emp.typeSalary === 2,
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-6 h-6 text-purple-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-gray-900">
                Record Monthly Salary
              </h2>
              <p className="text-sm text-gray-500 truncate">{placeName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition flex-shrink-0 ml-3"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* ── Month pill ── */}
        {choosedMonth && (
          <div className="mx-5 mt-4 flex items-center gap-2 bg-purple-100 rounded-xl px-4 py-3">
            <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-purple-700">
              {moment(choosedMonth, "MM-YYYY").format("MMMM YYYY")}
            </span>
          </div>
        )}

        {/* ── Info card ── */}
        <div className="mx-5 mt-3 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800 leading-relaxed">
            Select employees who worked this month to record their monthly
            salary.
          </p>
        </div>

        {/* ── Employee list ── */}
        <div className="flex-1 overflow-y-auto mt-4 px-5">
          {monthlyEmployees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Users className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-lg font-semibold text-gray-500 mb-2">
                No Monthly Employees
              </p>
              <p className="text-sm text-gray-400 text-center leading-relaxed">
                Create monthly employees first to record their salary.
              </p>
            </div>
          ) : (
            <div className="space-y-3 pb-4">
              {monthlyEmployees.map((employee) => {
                const isSelected = isEmployeeSelected(employee.id);
                return (
                  <button
                    key={employee.id}
                    onClick={() => toggleEmployee(employee)}
                    className={`w-full text-left rounded-2xl p-4 border-2 transition-all ${
                      isSelected
                        ? "bg-purple-50 border-purple-500"
                        : "bg-gray-50 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Checkbox */}
                      <div
                        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition ${
                          isSelected
                            ? "bg-purple-600 border-purple-600"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {isSelected && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>

                      {/* Employee info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 text-base truncate">
                              {employee.name}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Fingerprint className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-400">
                                ID: {employee.id}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Salary badge */}
                        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 w-fit">
                          <DollarSign className="w-4 h-4 text-purple-600" />
                          <span className="text-base font-bold text-purple-700">
                            ₪{employee.monthlySalary?.toLocaleString() || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Summary bar ── */}
        {selectedEmployees.length > 0 && (
          <div className="mx-5 mt-3 bg-gray-50 border border-gray-200 rounded-2xl p-4">
            <div className="flex items-center">
              {/* Selected count */}
              <div className="flex-1 flex flex-col items-center gap-1">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  <span className="text-xs text-gray-500 font-medium">
                    Selected
                  </span>
                </div>
                <span className="text-2xl font-bold text-purple-600">
                  {selectedEmployees.length}
                </span>
              </div>

              {/* Divider */}
              <div className="w-px h-14 bg-gray-200 mx-4" />

              {/* Total amount */}
              <div className="flex-1 flex flex-col items-center gap-1">
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-green-500" />
                  <span className="text-xs text-gray-500 font-medium">
                    {t("total")}
                  </span>
                </div>
                <span className="text-2xl font-bold text-green-600">
                  ₪{totalAmount.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Footer buttons ── */}
        <div className="flex gap-3 px-5 pt-4 pb-5 border-t border-gray-100 mt-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 rounded-2xl transition disabled:opacity-50"
          >
            {t("cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedEmployees.length === 0 || loading}
            className={`flex-[2] flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold transition shadow-md ${
              selectedEmployees.length === 0 || loading
                ? "bg-gray-300 text-gray-400 cursor-not-allowed shadow-none"
                : "bg-purple-600 hover:bg-purple-700 text-white"
            }`}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t("loading")}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>{t("paid")}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin   { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
